import { z } from 'zod';
import { IdSchema, TimestampSchema } from './encoding.js';
import { DocumentContentTypeSchema, DocumentStatusSchema } from './documents.js';
/**
 * Internal chat contracts (FRD §4.9, FRD-CORE-045 to 060 and CORE-067; CLR-36, CLR-62; SDD §29).
 *
 * A conversation is one-to-one or a group. Everything here is reached only through a current
 * participation, or — for a group only — through group administration (SDD §29.3).
 */
export const CHAT_CONVERSATION_KINDS = ['one_to_one', 'group'];
export const ChatConversationKindSchema = z.enum(CHAT_CONVERSATION_KINDS);
export const CHAT_CONVERSATION_STATUSES = ['active', 'archived'];
export const ChatConversationStatusSchema = z.enum(CHAT_CONVERSATION_STATUSES);
export const CHAT_MESSAGE_STATUSES = ['visible', 'deleted', 'removed'];
export const ChatMessageStatusSchema = z.enum(CHAT_MESSAGE_STATUSES);
/** The longest message body (SDD §29.2). */
export const CHAT_MESSAGE_MAX_LENGTH = 4000;
/** A person, as chat shows them: an id and a display name, never an e-mail address. */
export const ChatPersonSchema = z.object({
    userId: IdSchema,
    displayName: z.string(),
});
/** One of the caller's conversations (`GET /api/v1/chat/conversations`). */
export const ChatConversationSchema = z.object({
    id: IdSchema,
    kind: ChatConversationKindSchema,
    /** The group's title, or the other participant's name for a one-to-one conversation. */
    label: z.string(),
    status: ChatConversationStatusSchema,
    /** The other participant of a one-to-one conversation; null for a group. */
    otherUser: ChatPersonSchema.nullable(),
    participantCount: z.number().int().min(0),
    unreadCount: z.number().int().min(0),
    lastMessageAt: TimestampSchema.nullable(),
    /** False for an archived group: it is read-only (FRD-CORE-057). */
    canPost: z.boolean(),
});
/** A conversation with its current participants (`GET /api/v1/chat/conversations/:id`). */
export const ChatConversationDetailSchema = ChatConversationSchema.extend({
    participants: z.array(ChatPersonSchema),
});
/** A message. A deleted or removed message has no body: the placeholder is shown instead. */
/**
 * An attachment as a message shows it (SDD §29.7). The file itself is downloaded from
 * `GET /api/v1/documents/{id}/content` once `available`.
 */
export const ChatAttachmentSchema = z.object({
    documentId: IdSchema,
    title: z.string(),
    fileName: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int().min(0),
    status: DocumentStatusSchema,
});
export const ChatMessageSchema = z.object({
    id: IdSchema,
    conversationId: IdSchema,
    author: ChatPersonSchema,
    body: z.string().nullable(),
    status: ChatMessageStatusSchema,
    postedAt: TimestampSchema,
    editedAt: TimestampSchema.nullable(),
    mentions: z.array(ChatPersonSchema),
    /**
     * The message's current attachments, oldest first, so the conversation shows them — and plays a
     * voice message — in place (FRD-CORE-050, CORE-067). Empty for a deleted or removed message.
     */
    attachments: z.array(ChatAttachmentSchema),
    /** The caller wrote it, so may edit and delete it (FRD-CORE-051). */
    mine: z.boolean(),
    /** For `If-Match` on an edit (SDD §7.3). */
    version: z.number().int(),
});
const MessageBodySchema = z.string().trim().min(1).max(CHAT_MESSAGE_MAX_LENGTH);
/** Messages before or after a message id; newest first when paging back, oldest first after. */
export const ChatMessagesQuerySchema = z.strictObject({
    after: IdSchema.optional(),
    before: IdSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
});
export const StartOneToOneRequestSchema = z.strictObject({ userId: IdSchema });
export const PostChatMessageRequestSchema = z.strictObject({
    body: MessageBodySchema,
    /** Chosen from the participant list, never parsed from the text (SDD §29.4). */
    mentionUserIds: z.array(IdSchema).max(50).optional(),
});
/**
 * Requires `If-Match`. `mentionUserIds` adds mentions (FRD-CORE-053): people already mentioned stay
 * mentioned, and only those added are notified. Omitted, the mentions are unchanged.
 */
export const EditChatMessageRequestSchema = z.strictObject({
    body: MessageBodySchema,
    mentionUserIds: z.array(IdSchema).max(50).optional(),
});
export const MarkChatReadRequestSchema = z.strictObject({ lastMessageId: IdSchema });
export const ChatUnreadSchema = z.object({ unread: z.number().int().min(0) });
/** The names-only directory of active users (SDD §29.3). */
export const ChatDirectoryQuerySchema = z.strictObject({ q: z.string().trim().min(1).max(100) });
/** Chat settings the screens need (`GET /api/v1/chat/settings`). */
export const ChatSettingsSchema = z.object({
    /** The document type chat attachments use; null until an administrator configures it. */
    attachmentDocumentTypeCode: z.string().nullable(),
    /** The content types that document type accepts; empty while it is not configured. */
    attachmentContentTypes: z.array(DocumentContentTypeSchema),
});
// ── Group administration (privileged, `platform.chat_group`) ─────────────────────────────────
export const ChatGroupSchema = z.object({
    id: IdSchema,
    title: z.string(),
    status: ChatConversationStatusSchema,
    memberCount: z.number().int().min(0),
    createdAt: TimestampSchema,
    /** For `If-Match` on a change (SDD §7.3). */
    version: z.number().int(),
});
export const ChatGroupDetailSchema = ChatGroupSchema.extend({
    members: z.array(ChatPersonSchema),
});
const GroupTitleSchema = z.string().trim().min(1).max(100);
export const CreateChatGroupRequestSchema = z.strictObject({
    title: GroupTitleSchema,
    memberUserIds: z.array(IdSchema).max(500).optional(),
});
/** Rename or archive. Requires `If-Match`. Archiving is one-way in Release 1. */
export const UpdateChatGroupRequestSchema = z
    .strictObject({
    title: GroupTitleSchema.optional(),
    status: z.literal('archived').optional(),
})
    .refine((v) => v.title !== undefined || v.status !== undefined, {
    message: 'Give a title or a status',
});
export const AddChatGroupMemberRequestSchema = z.strictObject({ userId: IdSchema });
//# sourceMappingURL=chat.js.map